import { useCallback } from 'react';

import { format } from 'date-fns/format';
import { startOfDay } from 'date-fns/startOfDay';

import { dateHelpers } from '@nocowanie/core/helpers';
import { useSearchCookie } from '@nocowanie/common-ui/hooks';

import {
    initSearchFromUrlAction,
    setFiltersVisibilityAction,
    useAppDispatch,
} from '@app/app-state';
import { environment } from '@app/environments/environment';
import { parseSearchParams } from '@app/helpers/search-params.helpers';
import { defaultSearchCriteria, SearchFilters } from '@app/models';

interface CmsDates {
    checkIn?: string | null;
    checkOut?: string | null;
    flexDates?: number | null;
}

/**
 * Hook to initialize search state from URL parameters
 * @returns Function to initialize search from URL
 */
export function useSearchInitializer({ autoOpenFiltersOnDesktop = false }) {
    const dispatch = useAppDispatch();
    const { getSearchCriteriaFromCookie } = useSearchCookie(environment.COOKIE_DOMAIN);

    return useCallback(
        async (
            searchParams: URLSearchParams | Record<string, string>,
            campaignListingId?: string,
            isMobileRes?: boolean,
            cmsDates?: CmsDates,
            filtersOverride?: SearchFilters,
        ): Promise<void> => {
            const { searchCriteria, filters, page } = parseSearchParams(searchParams);

            // Apply date priority: URL > CMS (if applies) > cookie
            if (!searchCriteria.checkInDate) {
                const cookieCriteria = getSearchCriteriaFromCookie();

                if (
                    cmsDates?.checkIn &&
                    cmsDates?.checkOut &&
                    dateHelpers.isValidRange(
                        startOfDay(new Date(cmsDates.checkIn)),
                        startOfDay(new Date(cmsDates.checkOut)),
                    )
                ) {
                    searchCriteria.checkInDate = cmsDates.checkIn;
                    searchCriteria.checkOutDate = cmsDates.checkOut;
                    searchCriteria.flexDates = cmsDates.flexDates ?? environment.DEFAULT_FLEX_DATES;
                } else if (
                    cookieCriteria?.dates?.checkInDate &&
                    cookieCriteria?.dates?.checkOutDate &&
                    dateHelpers.isValidRange(
                        cookieCriteria.dates.checkInDate,
                        cookieCriteria.dates.checkOutDate,
                    )
                ) {
                    searchCriteria.checkInDate = format(
                        cookieCriteria.dates.checkInDate,
                        'yyyy-MM-dd',
                    );
                    searchCriteria.checkOutDate = format(
                        cookieCriteria.dates.checkOutDate,
                        'yyyy-MM-dd',
                    );
                    if (cookieCriteria.flexDates !== undefined) {
                        searchCriteria.flexDates = cookieCriteria.flexDates;
                    }
                } else {
                    // No dates from URL, cookie, or CMS — explicitly unset to prevent
                    // defaultSearchCriteria dates from leaking into the URL on filter/page changes.
                    searchCriteria.checkInDate = undefined;
                    searchCriteria.checkOutDate = undefined;
                    searchCriteria.flexDates = undefined;
                }

                if (cookieCriteria?.guests) {
                    const adultsCount =
                        cookieCriteria.guests.adultsCount ??
                        searchCriteria.guests?.adultsCount ??
                        defaultSearchCriteria.guests.adultsCount;
                    const childrenCount =
                        cookieCriteria.guests.childrenCount ??
                        searchCriteria.guests?.childrenCount ??
                        defaultSearchCriteria.guests.childrenCount;
                    searchCriteria.guests = {
                        adultsCount,
                        childrenCount,
                        childrenAges:
                            cookieCriteria.guests.childrenAges ??
                            searchCriteria.guests?.childrenAges ??
                            [],
                        guestCount: adultsCount + childrenCount,
                    };
                }
            }

            dispatch(
                initSearchFromUrlAction({
                    searchCriteria,
                    filters: {
                        ...filters,
                        ...filtersOverride,
                    },
                    page,
                    campaignListingId,
                }),
            );

            if (isMobileRes !== undefined && autoOpenFiltersOnDesktop) {
                // Open filters on desktop
                const shouldOpenFilters = !isMobileRes;
                dispatch(setFiltersVisibilityAction({ open: shouldOpenFilters }));
            }
        },
        [autoOpenFiltersOnDesktop, dispatch, getSearchCriteriaFromCookie],
    );
}
